import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import MicIcon from '@mui/icons-material/Mic';
import PaletteIcon from '@mui/icons-material/Palette';
import UndoIcon from '@mui/icons-material/Undo';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';

import { Box, Button, Divider, FormControl, IconButton, InputLabel, MenuItem, Paper, Select, SelectChangeEvent, Stack, Typography } from '@mui/material';

import ToggleButton, { toggleButtonClasses } from '@mui/material/ToggleButton';
import ToggleButtonGroup, { toggleButtonGroupClasses } from '@mui/material/ToggleButtonGroup';
import { styled } from '@mui/material/styles';

import { PhraseHighlight } from '@/components/displays/PhraseHighlight';
import { ImagePicker } from '@/components/inputs/ImagePicker/ImagePicker';
import { BUILTIN_IMG_SRC } from '@/components/inputs/ImagePicker/source';
import { WordPicker } from '@/components/inputs/WordPicker/WordPicker';
import { Popover } from '@/components/utils/Popover/Popover';
import { CANVAS_SIZE } from '@/constant';
import { SlideComponentHkcsQ3 } from '@/types/slide';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonPlaceholder } from '../components/ButtonPlaceholder';
import { ImageSelector } from '../components/ImageSelector';
import { Pronunciation } from '../components/Pronunciation';

import { CharacterOptionsPicker } from './CharacterOptionsPicker';

interface Props {
    anchorEl: HTMLElement | null;
    value: SlideComponentHkcsQ3;
    slotProps?: { paper?: { ref: React.Ref<HTMLDivElement> } };
    onClose: () => void;
    onUpdate?: (model: SlideComponentHkcsQ3) => void;
    onUploadImage?: (file: File) => Promise<string>;
}

export const EditPopover: React.FC<Props> = ({ anchorEl, value, slotProps, onClose, onUpdate, onUploadImage }) => {
    const { t } = useTranslation();
    const open = Boolean(anchorEl);

    // ref
    const wordPickerRef = useRef<HTMLDivElement | null>(null);
    const wordSelectorRef = useRef<HTMLElement | null>(null);
    const imagePickerRef = useRef<HTMLDivElement | null>(null);
    const characterOptionsPickerRef = useRef<HTMLDivElement | null>(null);

    // ui state
    const [wordPickerAnchorEl, setWordPickerAnchorEl] = useState<HTMLElement | null>(null);
    const [imagePickerAnchorEl, setImagePickerAnchorEl] = useState<HTMLElement | null>(null);

    const [characterOptionsPickerAnchorEl, setCharacterOptionsPickerAnchorEl] = useState<HTMLElement | null>(null);

    const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
        gap: 5,
        [`& .${toggleButtonGroupClasses.firstButton}, & .${toggleButtonGroupClasses.middleButton}`]: {
            borderTopRightRadius: (theme.vars || theme).shape.borderRadius,
            borderBottomRightRadius: (theme.vars || theme).shape.borderRadius,
        },
        [`& .${toggleButtonGroupClasses.lastButton}, & .${toggleButtonGroupClasses.middleButton}`]: {
            borderTopLeftRadius: (theme.vars || theme).shape.borderRadius,
            borderBottomLeftRadius: (theme.vars || theme).shape.borderRadius,
            borderLeft: `1px solid ${(theme.vars || theme).palette.divider}`,
        },
        [`& .${toggleButtonGroupClasses.lastButton}.${toggleButtonClasses.disabled}, & .${toggleButtonGroupClasses.middleButton}.${toggleButtonClasses.disabled}`]: {
            borderLeft: `1px solid ${(theme.vars || theme).palette.action.disabledBackground}`,
        },
    }));

    const checkLegacyLogic = (soundLength: number, phraseLength: number) => {
        return soundLength > phraseLength && soundLength % phraseLength === 0;
    };

    const calculateRatio = (phraseSound: string, phrase: string) => {
        const wordCount = phraseSound.split(' ').length;
        const cleanedPhraseLength = phrase.replace(/[（）()]/g, '').length;
        return Math.floor(wordCount / cleanedPhraseLength);
    };

    return (
        <>
            <Popover
                title={t('slide.legacy.hkcsQ3.title')}
                open={open}
                anchorEl={anchorEl}
                onClose={() => {
                    onClose();
                }}
                anchorOrigin={{
                    vertical: 'center',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'center',
                    horizontal: 'center',
                }}
                slotProps={{
                    paper: {
                        ref: slotProps?.paper?.ref,
                        sx: {
                            display: 'flex',
                            p: 2,
                            width: CANVAS_SIZE.width,
                            height: CANVAS_SIZE.height,
                            overflowX: 'scroll',
                        },
                    },
                }}
            >
                <Box sx={{ display: 'flex', height: '100%', flexDirection: 'column', width: CANVAS_SIZE.width - 32 }}>
                    <Typography variant='h4' sx={{ fontSize: 24, color: 'text.secondary' }} gutterBottom>
                        {t('slide.legacy.hkcsQ3.label.description')}
                    </Typography>
                    <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', flex: 1, flexDirection: 'row', gap: 1, height: '60%' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', width: 296 }}>
                                <Box
                                    sx={(theme) => ({
                                        display: 'flex',
                                        flexDirection: 'row',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        width: '100%',
                                        height: 136,
                                        borderRadius: 1,
                                        backgroundColor: theme.palette.grey[200],
                                        ...theme.applyStyles('dark', {
                                            backgroundColor: theme.palette.grey[600],
                                        }),
                                    })}
                                >
                                    <Box
                                        sx={(theme) => ({
                                            flex: '1 1 auto',
                                            minWidth: 0,
                                            borderRadius: 1,
                                            backgroundColor: theme.palette.grey[200],
                                            ...theme.applyStyles('dark', {
                                                backgroundColor: theme.palette.grey[600],
                                            }),
                                        })}
                                    >
                                        {value.content?.characterList && value.content?.characterList.length > 0 ? (
                                            <Stack
                                                sx={{
                                                    display: 'flex',
                                                    flexGrow: 1,
                                                    height: '100%',
                                                }}
                                            >
                                                <Paper
                                                    sx={(theme) => ({
                                                        flexGrow: 1,
                                                        padding: 1,
                                                        marginLeft: 1,
                                                        borderRadius: 1,
                                                        height: '120px',

                                                        backgroundColor: (theme.vars || theme).palette.grey[100],
                                                        ...theme.applyStyles('dark', {
                                                            backgroundColor: (theme.vars || theme).palette.grey[700],
                                                        }),
                                                    })}
                                                >
                                                    <Divider textAlign='left'>
                                                        <Typography variant='caption'>{t('slide.legacy.hkcsQ3.label.boundMorpheme')}</Typography>
                                                    </Divider>
                                                    <Stack
                                                        direction={'row'}
                                                        spacing={1}
                                                        sx={{
                                                            width: '100%',
                                                            maxWidth: '100%',
                                                            mt: 1.5,
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: '100%',
                                                                maxWidth: '100%',

                                                                overflowX: 'auto',
                                                                overflowY: 'hidden',

                                                                whiteSpace: 'nowrap',
                                                                paddingY: 1,
                                                                '&::-webkit-scrollbar': {
                                                                    height: '6px',
                                                                },
                                                                '&::-webkit-scrollbar-thumb': {
                                                                    background: '#888',
                                                                    borderRadius: '3px',
                                                                },
                                                            }}
                                                        >
                                                            <ToggleButtonGroup
                                                                exclusive
                                                                value={value.content?.characterList?.[value.content?.character]?.character}
                                                                onChange={(_event, newValue) => {
                                                                    if (!newValue) {
                                                                        return;
                                                                    }

                                                                    const charIdx = value.content?.characterList?.findIndex((v) => v.character === newValue);
                                                                    const char = value.content?.characterList?.[charIdx];

                                                                    if (charIdx !== -1 && char) {
                                                                        const updatedContent = {
                                                                            ...value.content,
                                                                            character: charIdx,
                                                                        };
                                                                        const updateModel = { ...value, content: updatedContent };

                                                                        onUpdate?.(updateModel);
                                                                    }
                                                                }}
                                                                sx={{
                                                                    height: 40,
                                                                }}
                                                            >
                                                                {value.content?.phrase.split('').map((char) => {
                                                                    return <ToggleButton value={char}>{char}</ToggleButton>;
                                                                })}
                                                            </ToggleButtonGroup>
                                                        </Box>
                                                    </Stack>
                                                </Paper>
                                            </Stack>
                                        ) : (
                                            // padding
                                            <Box />
                                        )}
                                    </Box>
                                    <Box
                                        sx={{
                                            height: '100%',
                                            aspectRatio: 1,
                                        }}
                                    >
                                        <ImageSelector
                                            onSelect={(_, selectorRef) => setImagePickerAnchorEl(selectorRef.current)}
                                            src={value.content.imageURL ? value.content.imageURL : value.content.image ? `/asset/characters/${value.content.image}/image.png` : undefined}
                                        />
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                                    <Box sx={{ flex: '0 0 64px', display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                                        <ButtonPlaceholder icon={<VolumeUpIcon sx={{ color: 'divider' }} fontSize='large' />} />
                                    </Box>
                                    <Box
                                        sx={(theme) => ({
                                            width: '100%',
                                            height: 80,
                                            padding: 1,
                                            borderRadius: 1,
                                            backgroundColor: theme.palette.grey[200],
                                            ...theme.applyStyles('dark', {
                                                backgroundColor: theme.palette.grey[600],
                                            }),
                                        })}
                                        ref={wordSelectorRef}
                                    >
                                        <Box
                                            sx={(theme) => ({
                                                borderRadius: 1,
                                                display: 'flex',
                                                flexDirection: 'row',
                                                justifyContent: 'center',
                                                gap: 1,
                                                height: '100%',
                                                p: 1,
                                                position: 'relative',
                                                backgroundColor: theme.palette.grey[100],
                                                ...theme.applyStyles('dark', {
                                                    backgroundColor: theme.palette.grey[700],
                                                }),
                                            })}
                                        >
                                            {value.content.phrase ? (
                                                <>
                                                    <Box sx={{ flex: 1, alignContent: 'center' }}>
                                                        <PhraseHighlight phrase={value.content.phrase} highlight={value.content.phrase} underline={false} />
                                                    </Box>
                                                    <IconButton
                                                        sx={{ position: 'absolute', top: 0, right: 0, m: 0.5 }}
                                                        onClick={() => {
                                                            setWordPickerAnchorEl(wordSelectorRef.current);
                                                        }}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                </>
                                            ) : (
                                                <Button
                                                    variant='contained'
                                                    onClick={() => {
                                                        setWordPickerAnchorEl(wordSelectorRef.current);
                                                    }}
                                                    sx={{
                                                        minWidth: '50%',
                                                        bottom: 0,
                                                        left: 0,
                                                        right: 0,
                                                        m: 1,
                                                    }}
                                                >
                                                    {t('slide.legacy.character.selectWord')}
                                                </Button>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>

                                <Stack direction={'row'} spacing={1} sx={{ my: 0.5, display: 'flex' }}>
                                    {value.content.phrase && value.content.phraseSoundGroup && (
                                        <FormControl fullWidth>
                                            <InputLabel id='cantonese-pronunciation-select-label'>{t('common.cantonese')}</InputLabel>
                                            <Select
                                                labelId='cantonese-pronunciation-select-label'
                                                id='cantonese-pronunciation-select-id'
                                                value={value.content.phraseSoundGroup?.jyutping[0] ? value.content.phraseSoundGroup.jyutping[0]?.startIndex.toString() : ''}
                                                label={t('common.cantonese')}
                                                onChange={(event: SelectChangeEvent) => {
                                                    if (value.content?.phraseSoundGroup) {
                                                        const phraseSoundGroup = {
                                                            ...value.content?.phraseSoundGroup,
                                                            ...(value.content?.phraseSoundGroup?.jyutping && {
                                                                jyutping: [{ startIndex: parseInt(event.target.value) }],
                                                            }),
                                                        };

                                                        const updatedContent = {
                                                            ...value.content,
                                                            phraseSoundGroup: phraseSoundGroup,
                                                        };
                                                        const updateModel = { ...value, content: updatedContent };

                                                        onUpdate?.(updateModel);
                                                    }
                                                }}
                                            >
                                                {value.content.phraseSound?.jyutping?.map((phraseSound, index) => {
                                                    const isLegacyLogic = checkLegacyLogic(phraseSound.sound?.split(' ').length, value.content.phrase.length);

                                                    if (isLegacyLogic) {
                                                        return [...Array(calculateRatio(phraseSound.sound, value.content.phrase))].map((_, idx) => (
                                                            <MenuItem value={idx.toString()} key={idx} data-testid={`cantonese-option-${idx}`}>
                                                                <Stack direction='row' spacing={1} alignItems={'center'}>
                                                                    <Pronunciation
                                                                        checkLegacyLogic={checkLegacyLogic}
                                                                        phraseSound={value.content.phraseSound}
                                                                        phrase={value.content.phrase}
                                                                        groupIndex={idx}
                                                                        folder={'jyutping'}
                                                                    />
                                                                    <Typography justifyContent='center' justifyItems={'center'} justifySelf={'center'} align='center'>
                                                                        {t('slide.legacy.character.phraseSoundGroup', {
                                                                            i: idx + 1,
                                                                        })}
                                                                    </Typography>
                                                                </Stack>
                                                            </MenuItem>
                                                        ));
                                                    } else {
                                                        return (
                                                            <MenuItem value={index.toString()} key={index} data-testid={`cantonese-option-${index}`}>
                                                                <Stack direction='row' spacing={1} alignItems={'center'}>
                                                                    <Pronunciation
                                                                        checkLegacyLogic={checkLegacyLogic}
                                                                        phraseSound={value.content.phraseSound}
                                                                        phrase={value.content.phrase}
                                                                        groupIndex={index}
                                                                        folder={'jyutping'}
                                                                    />
                                                                    <Typography>
                                                                        {t('slide.legacy.character.phraseSoundGroup', {
                                                                            i: index + 1,
                                                                        })}
                                                                    </Typography>
                                                                </Stack>
                                                            </MenuItem>
                                                        );
                                                    }
                                                })}
                                            </Select>
                                        </FormControl>
                                    )}

                                    {value.content.phrase && value.content.phraseSoundGroup && (
                                        <FormControl fullWidth>
                                            <InputLabel id='putonghua-pronunciation-select-label'>{t('common.putonghua')}</InputLabel>
                                            <Select
                                                data-testid='putonghua-pronunciation-select'
                                                labelId='putonghua-pronunciation-select-label'
                                                id='putonghua-pronunciation-select-id'
                                                value={value.content.phraseSoundGroup?.pinyin[0] ? value.content.phraseSoundGroup.pinyin[0]?.startIndex.toString() : ''}
                                                label={t('common.putonghua')}
                                                onChange={(event: SelectChangeEvent) => {
                                                    if (value.content?.phraseSoundGroup) {
                                                        const phraseSoundGroup = {
                                                            ...value.content?.phraseSoundGroup,
                                                            ...(value.content?.phraseSoundGroup?.pinyin && {
                                                                pinyin: [{ startIndex: parseInt(event.target.value) }],
                                                            }),
                                                        };

                                                        const updatedContent = {
                                                            ...value.content,
                                                            phraseSoundGroup: phraseSoundGroup,
                                                        };
                                                        const updateModel = { ...value, content: updatedContent };

                                                        onUpdate?.(updateModel);
                                                    }
                                                }}
                                            >
                                                {value.content.phraseSound?.pinyin?.map((phraseSound, index) => {
                                                    const isLegacyLogic = checkLegacyLogic(phraseSound.sound?.split(' ').length, value.content.phrase.length);

                                                    if (isLegacyLogic) {
                                                        return [...Array(calculateRatio(phraseSound.sound, value.content.phrase))].map((_, idx) => (
                                                            <MenuItem value={idx.toString()} key={idx} data-testid={`putonghua-option-${idx}`}>
                                                                <Stack direction='row' spacing={1} alignItems={'center'}>
                                                                    <Pronunciation
                                                                        checkLegacyLogic={checkLegacyLogic}
                                                                        phraseSound={value.content.phraseSound}
                                                                        phrase={value.content.phrase}
                                                                        groupIndex={idx}
                                                                        folder={'pinyin'}
                                                                    />
                                                                    <Typography justifyContent='center' justifyItems={'center'} justifySelf={'center'} align='center'>
                                                                        {t('slide.legacy.character.phraseSoundGroup', {
                                                                            i: idx + 1,
                                                                        })}
                                                                    </Typography>
                                                                </Stack>
                                                            </MenuItem>
                                                        ));
                                                    } else {
                                                        return (
                                                            <MenuItem value={index.toString()} key={index} data-testid={`putonghua-option-${index}`}>
                                                                <Stack direction='row' spacing={1} alignItems={'center'}>
                                                                    <Pronunciation
                                                                        checkLegacyLogic={checkLegacyLogic}
                                                                        phraseSound={value.content.phraseSound}
                                                                        phrase={value.content.phrase}
                                                                        groupIndex={index}
                                                                        folder={'pinyin'}
                                                                    />
                                                                    <Typography>
                                                                        {t('slide.legacy.character.phraseSoundGroup', {
                                                                            i: index + 1,
                                                                        })}
                                                                    </Typography>
                                                                </Stack>
                                                            </MenuItem>
                                                        );
                                                    }
                                                })}
                                            </Select>
                                        </FormControl>
                                    )}
                                </Stack>

                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        gap: 2,
                                        width: '100%',
                                        overflow: 'hidden',
                                    }}
                                    ref={characterOptionsPickerRef}
                                >
                                    <Box sx={{ flex: '0 0 64px', display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                                        <ButtonPlaceholder icon={<VolumeUpIcon sx={{ color: 'divider' }} fontSize='large' />} />
                                    </Box>

                                    <Box
                                        sx={(theme) => ({
                                            flex: '1 1 auto',
                                            minWidth: 0,
                                            padding: 1,
                                            borderRadius: 1,
                                            backgroundColor: theme.palette.grey[200],
                                            ...theme.applyStyles('dark', {
                                                backgroundColor: theme.palette.grey[600],
                                            }),
                                        })}
                                    >
                                        {/*  */}

                                        <Stack
                                            sx={(theme) => ({
                                                position: 'relative',
                                                width: '100%',
                                                maxWidth: '100%',
                                                borderRadius: 1,
                                                paddingX: 1,
                                                height: 80,
                                                backgroundColor: theme.palette.grey[100],
                                                ...theme.applyStyles('dark', {
                                                    backgroundColor: theme.palette.grey[700],
                                                }),
                                            })}
                                        >
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    maxWidth: '100%',
                                                    overflowX: 'auto',
                                                    overflowY: 'hidden',

                                                    whiteSpace: 'nowrap',
                                                    paddingY: 1,
                                                    '&::-webkit-scrollbar': {
                                                        height: '6px',
                                                    },
                                                    '&::-webkit-scrollbar-thumb': {
                                                        background: '#888',
                                                        borderRadius: '3px',
                                                    },
                                                }}
                                            >
                                                <StyledToggleButtonGroup
                                                    aria-label='toggle options'
                                                    color='primary'
                                                    sx={{
                                                        display: 'inline-flex',
                                                        flexWrap: 'nowrap',
                                                        mt: 1,
                                                    }}
                                                >
                                                    {value.content.characterOptions?.map((data, index) => (
                                                        <Box
                                                            key={index}
                                                            sx={{
                                                                position: 'relative',
                                                                display: 'inline-flex',
                                                                flexShrink: 0,
                                                            }}
                                                        >
                                                            <ToggleButton
                                                                value={data.characterList[data.character]?.character || ''}
                                                                sx={{
                                                                    flexShrink: 0,
                                                                    minWidth: 'max-content',
                                                                    px: 2,
                                                                    // pr: 4,
                                                                    color: 'white',

                                                                    backgroundColor: data.isCorrect ? '#2E7D32' : '#D32F2F',

                                                                    '&:hover': {
                                                                        backgroundColor: data.isCorrect ? '#2E7D32' : '#D32F2F',
                                                                        cursor: 'default',
                                                                    },

                                                                    // Also disable hover for selected state
                                                                    '&.Mui-selected:hover': {
                                                                        backgroundColor: data.isCorrect ? '#2E7D32' : '#D32F2F',
                                                                    },
                                                                }}
                                                            >
                                                                {data.characterList[data.character]?.character}
                                                            </ToggleButton>
                                                        </Box>
                                                    ))}
                                                </StyledToggleButtonGroup>
                                            </Box>
                                            <IconButton
                                                data-testid='character-options-edit'
                                                sx={{ position: 'absolute', top: 0, right: 0, m: 0.5 }}
                                                onClick={() => {
                                                    setCharacterOptionsPickerAnchorEl(characterOptionsPickerRef.current);
                                                }}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        </Stack>
                                    </Box>
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    flex: 1,
                                    display: 'flex',
                                }}
                            >
                                <Box
                                    sx={{
                                        flex: 1,
                                        backgroundColor: 'white',
                                        borderRadius: 2,
                                        border: 2,
                                        borderColor: 'divider',
                                    }}
                                />
                                <Box sx={{ width: 109, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                                    <ButtonPlaceholder icon={<EditIcon sx={{ color: 'divider' }} fontSize='large' />} />
                                    <ButtonPlaceholder icon={<PaletteIcon sx={{ color: 'divider' }} fontSize='large' />} />
                                    <ButtonPlaceholder icon={<UndoIcon sx={{ color: 'divider' }} fontSize='large' />} />
                                    <ButtonPlaceholder icon={<DeleteOutlineIcon sx={{ color: 'divider' }} fontSize='large' />} />{' '}
                                </Box>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', height: '40%', pt: 1 }}>
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                                <ButtonPlaceholder icon={<MicIcon sx={{ color: 'divider' }} fontSize='large' />} />
                                <ButtonPlaceholder icon={<VolumeUpIcon sx={{ color: 'divider' }} fontSize='large' />} />
                            </Box>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateRows: 'repeat(2, 1fr)',
                                    gridTemplateColumns: 'repeat(7, 1fr)',
                                    gap: '1px',
                                    backgroundColor: 'divider',
                                    height: 'fit-content',
                                    width: 'fit-content',
                                    border: 1,
                                    borderColor: 'divider',
                                }}
                            >
                                {[...Array(14)].map((_, idx) => (
                                    <div
                                        key={idx}
                                        style={{
                                            height: '124px',
                                            width: '124px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            background: 'white',
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Popover>
            {imagePickerAnchorEl && (
                <ImagePicker
                    type='image'
                    title={t('slide.legacy.character.title')}
                    librarySources={BUILTIN_IMG_SRC}
                    anchorEl={imagePickerAnchorEl}
                    slotProps={{
                        paper: {
                            ref: imagePickerRef, // Attach the ref to the Paper element
                        },
                    }}
                    value={{
                        value: value?.content?.image || '',
                        imageURL: value?.content?.imageURL || '',
                    }}
                    onClose={() => {
                        setImagePickerAnchorEl(null);
                    }}
                    onUpdate={(settings) => {
                        const updatedContent: SlideComponentHkcsQ3['content'] = {
                            ...value.content,
                            image: settings.value,
                            imageURL: settings.imageURL,
                        };
                        const updateModel = { ...value, content: updatedContent, ...(settings.size ? { size: settings.size } : {}) };

                        onUpdate?.(updateModel);
                    }}
                    onUpload={onUploadImage}
                />
            )}
            {wordPickerAnchorEl && (
                <WordPicker
                    type='phrase'
                    title={t('slide.legacy.character.characterSearch')}
                    anchorEl={wordPickerAnchorEl}
                    slotProps={{
                        paper: {
                            ref: wordPickerRef, // Attach the ref to the Paper element
                        },
                    }}
                    value={{
                        characterList: value?.content?.characterList,
                        characterSound: value?.content?.characterSound,
                        phrase: value?.content?.phrase,
                        phraseSound: value?.content?.phraseSound,
                        phraseSoundGroup: value?.content?.phraseSoundGroup,
                    }}
                    onClose={() => {
                        setWordPickerAnchorEl(null);
                    }}
                    onUpdate={(settings) => {
                        const updatedContent: SlideComponentHkcsQ3['content'] = {
                            ...value.content,
                            character: 0, // reset character index
                            characterList: settings.characterList ?? [],
                            characterSound: settings.characterSound ?? { pinyin: [], jyutping: [] },
                            phrase: settings.phrase ?? '',
                            phraseSound: settings.phraseSound ?? { pinyin: [], jyutping: [] },
                            phraseSoundGroup: settings.phraseSoundGroup ?? { pinyin: [], jyutping: [] },
                        };
                        const updateModel: SlideComponentHkcsQ3 = { ...value, content: updatedContent };

                        onUpdate?.(updateModel);
                    }}
                />
            )}

            {characterOptionsPickerAnchorEl && (
                <CharacterOptionsPicker
                    anchorEl={characterOptionsPickerAnchorEl}
                    value={{
                        characterOptions: value.content.characterOptions,
                    }}
                    onClose={() => {
                        setCharacterOptionsPickerAnchorEl(null);
                    }}
                    onUpdate={(settings) => {
                        const updatedContent: SlideComponentHkcsQ3['content'] = {
                            ...value.content,

                            characterOptions: settings.characterOptions || [],
                        };
                        const updateModel: SlideComponentHkcsQ3 = { ...value, content: updatedContent };

                        onUpdate?.(updateModel);
                    }}
                />
            )}
        </>
    );
};
